// ListRow.tsx
import React, { useState } from 'react';
import { 
  EyeIcon as Eye, 
  MinusIcon as Minus, 
  PlusIcon as Plus, 
  Loader2Icon as Loader, 
  CheckCircle2Icon as CheckSquare, 
  Square as Square, 
  Target as Target 
} from '../../icons';
import { OrderObject } from '../types/objects';
import { statusConfig } from '../types/status';
import { ContractionModal } from './ContractionModal';

interface ListRowProps {
  obj: OrderObject;
  index: number;
  style: React.CSSProperties;
  onQuantityChange: (id: string, quantity: number) => void;
  onView: (id: string) => void;
  formatValue: (value: number) => string;
  onContractionChange: (id: string, contraction: string) => void;
  role?: string;
  onToggleInclude?: (id: string, include: boolean) => void;
  showContraction?: boolean; // ✅ NOVO: Controla exibição do botão de contração
}

export const ListRow = React.memo(({
  obj,
  index,
  style,
  onQuantityChange,
  onView,
  formatValue,
  onContractionChange,
  role,
  onToggleInclude,
  showContraction = true // ✅ Default: mostrar contração
}: ListRowProps) => {
  const [showContractionModal, setShowContractionModal] = useState(false);
  const [isFocusing, setIsFocusing] = useState(false);
  
  // Verificar se é usuário de produção
  const isProducao = role === 'producao';
  
  const status = statusConfig[obj.status];
  const isAlertStatus = obj.status === 'error' || obj.status === 'warning';
  const rowSurface = isAlertStatus ? status.bgColor : 'bg-navy-800/30';
  const leftBorderWidth = obj.status === 'error' ? 'border-l-4' : 'border-l-[3px]';

  const statusMessage = obj.message
    ? `${obj.status === 'error' ? 'ERRO' : obj.status === 'warning' ? 'ALERTA' : 'OK'}: ${obj.message}`
    : (obj.status === 'error'
      ? 'ERRO: Dimensões excedem o máximo permitido'
      : obj.status === 'warning'
        ? 'ALERTA: Largura muito pequena'
        : 'OK: Dimensões adequadas para impressão');

  const handleQuantityChange = (delta: number) => {
    const newValue = Math.max(1, obj.quantity + delta);
    onQuantityChange(obj.id, newValue);
  };

  const handleViewClick = () => {
    setIsFocusing(true);
    
    // Chamar a função de visualização com StaticID
    onView(obj.staticId);
    
    // Desativar o estado de loading após um tempo razoável
    setTimeout(() => {
      setIsFocusing(false);
    }, 800);
  };

  return (
    <>
      <div
        className={`
          flex flex-col 
          ${leftBorderWidth} border-t border-r border-b
          ${status.borderColor} border-t-navy-700/40 border-r-navy-700/40 border-b-navy-700/40
          ${rowSurface}
          w-full py-2 mb-2
          relative
          min-w-0
          transition-all duration-200
          rounded-r-md
          ${isAlertStatus ? status.hoverBg : 'hover:bg-navy-800/50 hover:border-t-navy-600/60 hover:border-r-navy-600/60 hover:border-b-navy-600/60'}
          shadow-sm
          ${obj.status === 'error' ? 'shadow-red-500/10' : ''}
        `}
        style={style}
        title={statusMessage}
      >
        {/* Linha principal: Número + Checkbox + Nome + Badge + Controles */}
        <div className="flex items-center px-3 mb-1.5 min-w-0 gap-2">
          {/* Número sequencial */}
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-navy-700/70 border border-navy-600/50 flex items-center justify-center">
            <span className="text-[10px] font-bold text-gray-300">{index}</span>
          </div>
          
          {/* Checkbox include */}
          <button
            onClick={() => onToggleInclude && onToggleInclude(obj.id, !(obj.include ?? true))}
            className="text-gray-300 hover:text-yellow-400 flex-shrink-0"
            title={(obj.include ?? true) ? 'Excluir da exportação' : 'Incluir na exportação'}
          >
            {(obj.include ?? true) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          </button>
          
          {/* Nome do objeto */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`text-white font-medium text-sm truncate flex-1 min-w-0 ${obj.include === false ? 'opacity-50 line-through' : ''}`} title={obj.nome}>
              {obj.nome}
            </span>
          </div>
          
          {/* Badge de exportIndex */}
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${
              obj.exportIndex != null
                ? 'bg-emerald-700/30 text-emerald-200 border-emerald-500/40'
                : 'bg-navy-700 text-gray-300 border-navy-600'
            }`}
            title={
              obj.exportIndex != null
                ? (typeof obj.pageIndex === 'number'
                    ? `Ordem de exportação: página ${obj.pageIndex}, posição ${obj.exportIndex}`
                    : `Ordem de exportação: posição ${obj.exportIndex}`)
                : 'Ordem de exportação indisponível'
            }
          >
            {obj.exportIndex != null
              ? (typeof obj.pageIndex === 'number' ? `P${obj.pageIndex} - #${obj.exportIndex}` : `#${obj.exportIndex}`)
              : '—'}
          </span>
          
          {/* Controles de quantidade */}
          <div className={`flex items-center bg-navy-700/50 rounded flex-shrink-0 ${isProducao ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <button
              onClick={() => !isProducao && handleQuantityChange(-1)}
              className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white"
              title="Diminuir quantidade"
            >
              <Minus className="w-3 h-3" />
            </button>
            <input
              type="number"
              value={obj.quantity}
              min={1}
              onChange={e => !isProducao && onQuantityChange(obj.id, parseInt(e.target.value) || 1)}
              className="w-7 h-5 px-0 bg-transparent text-white text-xs text-center focus:outline-none no-spinner"
              title="Quantidade"
              disabled={isProducao}
            />
            <button
              onClick={() => !isProducao && handleQuantityChange(1)}
              className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white"
              title="Aumentar quantidade"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          
          {/* Botões de ação */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleViewClick}
              disabled={isFocusing}
              className={`w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white
                        ${isFocusing ? 'animate-pulse bg-blue-900/30 rounded-full' : ''}`}
              title="Visualizar no CorelDraw"
            >
              {isFocusing ? 
                <Loader className="w-[18px] h-[18px] animate-spin" /> : 
              <Eye className="w-[18px] h-[18px]" />
              }
            </button>
            {showContraction && (
              <button
                onClick={() => setShowContractionModal(true)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white"
                title="Editar contração"
              >
                <Target className="w-[18px] h-[18px]" />
              </button>
            )}
          </div>
        </div>

        {/* Linha secundária: Dimensões + Contração + Total */}
        <div className="flex items-center justify-between px-3 gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`text-xs font-medium ${status.dimTextColor ?? 'text-gray-300'}`}>
              {formatValue(obj.width)} x {formatValue(obj.height)}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {showContraction && (
              <span className="text-xs text-gray-400">
                {obj.contraction || '0px'}
              </span>
            )}
            <span className="text-xs text-gray-300 font-medium">
              Total: {obj.quantity} un
            </span>
          </div>
        </div>
      </div>

      {/* Modal de contração */}
      {showContractionModal && (
        <ContractionModal
          objectName={obj.nome}
          currentValue={obj.contraction || '0px'}
          onClose={() => setShowContractionModal(false)}
          onChange={(value) => {
            onContractionChange(obj.id, value);
            setShowContractionModal(false);
          }}
        />
      )}
    </>
  );
});

ListRow.displayName = 'ListRow';
